// Approximates partial-invert dark-mode clients (Apple Mail macOS/iOS,
// Outlook.com / New Outlook): puts the document on a dark canvas and
// selectively replaces near-white surfaces and near-black text via
// attribute selectors. Saturated brand colors are left alone, matching
// what those clients actually do.
//
// The selectors below cover values commonly authored as "near white" and
// "near black" in real templates so the preview catches "soft black" body
// copy and off-white panels — not just literal `#fff`/`#000`.

const DARK_BG = "#1f1f1f";
const LIGHT_FG = "#e8e8e8";

export const DARK_BACKGROUND = "#1a1a1a";

// Near-white background values commonly authored in email templates. These
// match the colors clients like Apple Mail / Outlook.com replace when they
// detect a "light surface"; we mirror their rough threshold.
const NEAR_WHITE_VALUES = [
  "#fff",
  "#ffffff",
  "white",
  "#fefefe",
  "#fdfdfd",
  "#fcfcfc",
  "#fbfbfb",
  "#fafafa",
  "#f9f9f9",
  "#f8f8f8",
  "#f7f7f7",
  "#f6f6f6",
  "#f5f5f5",
  "#f4f4f4",
  "#f3f3f3",
  "#f2f2f2",
  "#f1f1f1",
  "#f0f0f0",
];

// Near-black text values. Includes common "soft black" shades used for
// body copy.
const NEAR_BLACK_VALUES = [
  "#000",
  "#000000",
  "black",
  "#010101",
  "#020202",
  "#0a0a0a",
  "#111",
  "#111111",
  "#161616",
  "#1a1a1a",
  "#1c1c1c",
  "#1d1d1d",
  "#1f1f1f",
  "#212121",
  "#222",
  "#222222",
  "#262626",
  "#2a2a2a",
  "#2b2b2b",
  "#2c2c2c",
  "#2d2d2d",
  "#2e2e2e",
  "#2f2f2f",
  "#333",
  "#333333",
];

function buildInlineStyleValueSelectors({
  properties,
  values,
}: {
  properties: string[];
  values: string[];
}): string[] {
  return properties.flatMap((property) =>
    values.flatMap((value) => [
      `[style^="${property}:${value};" i]`,
      `[style^="${property}: ${value};" i]`,
      `[style^="${property}:${value} " i]`,
      `[style^="${property}: ${value} " i]`,
      `[style="${property}:${value}" i]`,
      `[style="${property}: ${value}" i]`,
      `[style*=";${property}:${value};" i]`,
      `[style*="; ${property}:${value};" i]`,
      `[style*=";${property}: ${value};" i]`,
      `[style*="; ${property}: ${value};" i]`,
      `[style*=";${property}:${value} " i]`,
      `[style*="; ${property}:${value} " i]`,
      `[style*=";${property}: ${value} " i]`,
      `[style*="; ${property}: ${value} " i]`,
    ]),
  );
}

const NEAR_WHITE_BACKGROUND_SELECTORS = [
  ...NEAR_WHITE_VALUES.map((value) => `[bgcolor="${value}" i]`),
  ...buildInlineStyleValueSelectors({
    properties: ["background", "background-color"],
    values: NEAR_WHITE_VALUES,
  }),
].join(", ");

const NEAR_BLACK_TEXT_SELECTORS = [
  ...NEAR_BLACK_VALUES.flatMap((value) => [
    `[color="${value}" i]`,
    `[text="${value}" i]`,
    `[link="${value}" i]`,
  ]),
  ...buildInlineStyleValueSelectors({
    properties: ["color"],
    values: NEAR_BLACK_VALUES,
  }),
].join(", ");

export const DARK_MODE_CSS = [
  `html { background-color: ${DARK_BG} !important; color-scheme: dark; }`,
  `body { color: ${LIGHT_FG} !important; }`,
  `${NEAR_WHITE_BACKGROUND_SELECTORS} { background-color: ${DARK_BG} !important; }`,
  `${NEAR_BLACK_TEXT_SELECTORS} { color: ${LIGHT_FG} !important; }`,
].join("\n");

export function htmlOptsIntoDarkMode(html: string): boolean {
  return /@media[^{]*prefers-color-scheme\s*:\s*dark/i.test(html);
}
